/**
 * The job's Visits band — `docs/visit-page.md` §26.5, Board 17 C and E.
 *
 * One band directly under the Visits tab's own header and above the list,
 * drawn by the job record's Visits tab — **not by the list engine**, which
 * this does not touch (do-not-substitute 0). It reads the quote check live
 * (`GET /api/v1/jobs/:id/visit-check`), so the office sees a missing visit the
 * moment it is missing; the night is what rings the bell.
 *
 * One line per approved quote, missing ones first (§27.7): green when every
 * visit is there (C), red with *Create the missing visit* when one is not (E).
 * A job with no approved quote draws nothing.
 */
import { useCallback, useEffect, useState } from 'react'
import type { VisitCheckQuote } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { VisitGlyph } from '../visit/glyphs.js'
import { BAND_BUTTON } from '../visit/band.js'
import { JOB_VISITS_BAND as WORDS } from './job-copy.js'

export function JobVisitsBand({ jobId, canCreate, onOpenQuote, onCreated }: {
  jobId: string
  /** Whoever may create visits on the job (`visits: edit`); absent otherwise, not greyed. */
  canCreate: boolean
  onOpenQuote: (quoteId: string) => void
  onCreated?: () => void
}) {
  const [check, setCheck] = useState<VisitCheckQuote[] | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    request<VisitCheckQuote[]>(`/jobs/${jobId}/visit-check`)
      .then((found) => setCheck(found))
      .catch(() => setCheck([]))
  }, [jobId])

  useEffect(() => { load() }, [load])

  const create = useCallback(async (quote: VisitCheckQuote) => {
    setBusy(quote.approvalId)
    setError(null)
    try {
      const made = await request<{ check: VisitCheckQuote[] }>(`/jobs/${jobId}/visit-check/create`, {
        method: 'POST',
        body: {
          approvalId: quote.approvalId,
          missing: quote.missing.map((one) => ({
            quoteVisitId: one.quoteVisitId, quoteLineId: one.quoteLineId, seriesIndex: one.seriesIndex,
          })),
        },
      })
      setCheck(made.check)
      onCreated?.()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause))
    } finally {
      setBusy(null)
    }
  }, [jobId, onCreated])

  if (!check || check.length === 0) return null

  return (
    <div className="overflow-hidden rounded-card border border-line">
      {check.map((quote, at) => (quote.missing.length === 0
        ? <AllThere key={quote.approvalId} quote={quote} first={at === 0} />
        : (
          <OneMissing
            key={quote.approvalId}
            quote={quote}
            first={at === 0}
            canCreate={canCreate}
            busy={busy === quote.approvalId}
            error={error}
            onCreate={() => void create(quote)}
            onOpen={() => onOpenQuote(quote.quoteId)}
          />
        )))}
    </div>
  )
}

/** Board 17 C. */
function AllThere({ quote, first }: { quote: VisitCheckQuote; first: boolean }) {
  return (
    <div
      data-as-drawn="vp-17 C"
      className={cn('flex items-center gap-2 bg-green-band-bg px-4 py-2.5 text-[12.5px] leading-[normal] tabular-nums text-green-band-text',
        !first && 'border-t border-line-faint')}
    >
      <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-accent text-white">
        <VisitGlyph name="check" size={11} stroke={3} />
      </span>
      <span>
        <b>{WORDS.allThere}</b> {WORDS.count(quote.reference, quote.present, quote.expected)}
      </span>
      <span className="flex-1" />
      <span className="text-[11.5px] text-text-muted">{WORDS.checked}</span>
    </div>
  )
}

/** Board 17 E. */
function OneMissing({ quote, first, canCreate, busy, error, onCreate, onOpen }: {
  quote: VisitCheckQuote
  first: boolean
  canCreate: boolean
  busy: boolean
  error: string | null
  onCreate: () => void
  onOpen: () => void
}) {
  const last = quote.missing.length - 1
  return (
    <div
      data-as-drawn="vp-17 E"
      className={cn('flex items-start gap-2.5 border-b border-danger-line bg-danger-bg px-4 py-3',
        'text-[12.5px] leading-[normal] tabular-nums text-danger', !first && 'border-t')}
    >
      <span className="mt-px inline-flex">
        <VisitGlyph name="warn" size={16} stroke={2.2} />
      </span>
      <div className="flex flex-1 flex-col">
        <b>{WORDS.missingHead(quote.reference, quote.expected, quote.missing.length)}</b>
        {quote.missing.map((one, at) => (
          <span key={`${one.quoteVisitId ?? one.quoteLineId}-${one.seriesIndex}`} className="mt-[3px] text-text-body">
            {WORDS.missing} <b>{one.name}</b>{' '}
            {WORDS.missingFacts(one.scheduleWords, one.days ?? 1, one.hours, one.amount)}
            {at === last && quote.foundAt && <>{' '}{WORDS.foundBy(quote.foundAt)}</>}
            {at === last && quote.beforeChecks && <>{' '}{WORDS.approvedBefore(quote.reference, quote.approvedAt)}</>}
          </span>
        ))}
        {error && <span className="mt-[3px]">{error}</span>}
        <div className="mt-[9px] flex items-center gap-2">
          {canCreate && (
            <button
              type="button"
              disabled={busy}
              onClick={onCreate}
              className={BAND_BUTTON.primary}
            >
              <VisitGlyph name="plus" size={14.5} stroke={2} />
              {WORDS.create}
            </button>
          )}
          <button
            type="button"
            onClick={onOpen}
            className={BAND_BUTTON.plain}
          >
            {WORDS.open(quote.reference)}
          </button>
        </div>
      </div>
    </div>
  )
}
